/**
 * @file CustomerProjectsList.tsx
 * @description The customer's own projects list on the dashboard. Each
 *   row shows the title, description, a colored status pill, and an
 *   amber "action needed" badge when the project is waiting on the
 *   customer (drafts ready to submit, quotes awaiting accept/reject).
 *   Once they accumulate more than a handful of projects a search
 *   input above the list keeps it scannable.
 * @module Components
 * @security The list comes from GET /projects which the server scopes
 *   to the calling user — customers only see their own projects.
 */

import type { ChangeEvent, JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../common/EmptyState';
import { SkeletonRows } from '../common/Skeleton';
import type { ProjectDto, ProjectStatus } from '../../lib/projects-api';
import { applyProjectFilters, EMPTY_PROJECT_FILTERS } from '../../lib/projects-filter';
import { MilestoneProgressChip } from '../projects/MilestoneProgressChip';
import { PriorityBadge } from '../projects/PriorityBadge';
import { ProjectDeadlineChip } from '../projects/ProjectDeadlineChip';

const STATUS_PILL_CLASS: Record<ProjectStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-blue-50 text-blue-700',
  reviewing: 'bg-blue-50 text-blue-700',
  quoted: 'bg-amber-50 text-amber-700',
  approved: 'bg-green-50 text-green-700',
  in_progress: 'bg-green-50 text-green-700',
  completed: 'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-red-50 text-red-700',
};

// Customer needs to act when the project is in 'draft' (their job to
// flesh out + submit) or 'quoted' (admin sent a price; their accept/
// reject decision unblocks the work). Other statuses are admin-driven.
const CUSTOMER_ACTION_STATUS: ReadonlySet<ProjectStatus> = new Set(['draft', 'quoted']);

// Show the search input only when there are enough projects to make
// scanning hard. Three or fewer rows fit on screen, so a search just
// adds noise.
const SEARCH_VISIBILITY_THRESHOLD = 4;

interface CustomerProjectsListProps {
  projects: ProjectDto[];
  loading: boolean;
  /** Slice 45 — when true the parent fetched with ?archived=true and
   *  the list includes archived projects (rendered muted). */
  includeArchived?: boolean;
  onToggleArchived?: (next: boolean) => void;
}

export function CustomerProjectsList({
  projects,
  loading,
  includeArchived = false,
  onToggleArchived,
}: CustomerProjectsListProps): JSX.Element {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(EMPTY_PROJECT_FILTERS);
  const showSearch = projects.length >= SEARCH_VISIBILITY_THRESHOLD;
  const visible = applyProjectFilters(projects, filters);
  const archivedCount = projects.filter((p) => p.archived_at !== null).length;

  if (loading && projects.length === 0) {
    return <SkeletonRows count={2} rowClassName="h-24 w-full" />;
  }
  if (projects.length === 0 && !includeArchived) {
    return (
      <EmptyState
        icon="📋"
        title={t('dashboard.empty_title')}
        description={t('dashboard.empty_help')}
      />
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-center">
        {showSearch ? (
          <input
            type="search"
            value={filters.query}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setFilters({ ...filters, query: e.target.value })
            }
            placeholder={t('dashboard.search_placeholder')}
            aria-label={t('dashboard.search_label')}
            className="flex-1 min-w-[12rem] px-3 py-2 text-sm"
          />
        ) : null}
        {onToggleArchived !== undefined ? (
          <label className="text-sm text-slate-600 inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => onToggleArchived(e.target.checked)}
            />
            {t('dashboard.show_archived')}
            {includeArchived && archivedCount > 0 ? (
              <span className="text-xs text-slate-500">({archivedCount})</span>
            ) : null}
          </label>
        ) : null}
      </div>
      {visible.length === 0 ? (
        <EmptyState icon="🔍" title={t('dashboard.search_no_match')} />
      ) : (
        <ul className="space-y-2">
          {visible.map((p) => (
            <ProjectRow key={p.id} project={p} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Archive-box glyph — inline SVG per design system (no emoji icons). */
function ArchiveIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
      <path
        d="M3.5 5.5h13v3h-13zM5 8.5V15h10V8.5M8.25 11.5h3.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProjectRow({ project }: { project: ProjectDto }): JSX.Element {
  const { t } = useTranslation();
  const archived = project.archived_at !== null;
  // Archived rows: don't show the "act now" badge (the project is
  // already tucked away; no nag) and dim the whole card.
  const needsAction = !archived && CUSTOMER_ACTION_STATUS.has(project.status);
  const pillClass = STATUS_PILL_CLASS[project.status];
  return (
    <li>
      <Link
        to={`/projects/${project.id}`}
        className={`block card p-4 hover:bg-slate-50/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
          archived ? 'opacity-60' : ''
        }`}
      >
        <div className="flex justify-between items-start gap-3 mb-2">
          <h2 className="font-semibold text-slate-900">{project.title}</h2>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className={`chip ${pillClass}`}>{t(`project.status.${project.status}`)}</span>
            <PriorityBadge priority={project.priority} />
            {archived ? (
              <span className="chip bg-slate-100 text-slate-600">
                <ArchiveIcon /> {t('project.archive.pill')}
              </span>
            ) : null}
            {needsAction ? (
              <span className="chip bg-amber-500 text-white">
                {t(`dashboard.action_badge.${project.status}`)}
              </span>
            ) : null}
          </div>
        </div>
        <p className="text-sm text-slate-600 line-clamp-2">{project.description}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {project.milestone_summary !== undefined ? (
            <MilestoneProgressChip summary={project.milestone_summary} />
          ) : null}
          <ProjectDeadlineChip status={project.status} desiredDeadline={project.desired_deadline} />
        </div>
      </Link>
    </li>
  );
}
